const BACKGROUND_OPTION_NAMES = new Set(['Background Colour', 'Background Color']);
const CUSTOM_COLOUR_VALUES = new Set(['Custom Colour', 'Custom Color']);
const COLOURS = new Map([
  ['Classic White', '#FFFFFF'],
  ['Sage Green', '#919E8B'],
  ['Blush Pink', '#E8B5B5'],
  ['Duck Egg Blue', '#C6D5DB'],
  ['Navy Grey', '#2C3840'],
]);

const controllers = new WeakMap();

function selectedBackground(section) {
  const option = [...section.querySelectorAll('variant-picker [data-option-name]')].find((candidate) => {
    if (!(candidate instanceof HTMLInputElement) && !(candidate instanceof HTMLOptionElement)) return false;
    if (!BACKGROUND_OPTION_NAMES.has(candidate.dataset.optionName ?? '')) return false;
    return candidate instanceof HTMLInputElement ? candidate.checked : candidate.selected;
  });

  if (option instanceof HTMLInputElement || option instanceof HTMLOptionElement) return option.value;

  const select = [...section.querySelectorAll('variant-picker select')].find((candidate) => {
    const optionName = candidate.name.replace(/^options\[/, '').replace(/\]$/, '');
    return BACKGROUND_OPTION_NAMES.has(optionName);
  });
  return select instanceof HTMLSelectElement ? select.value : 'Classic White';
}

function selectedFont(section) {
  const radio = section.querySelector('input[name="properties[_pnp_quote_font_style]"]:checked');
  return radio instanceof HTMLInputElement ? radio.value : 'classic_black';
}

function customHex(section) {
  const input = section.querySelector('.quote-colour-customizer__hex');
  if (!(input instanceof HTMLInputElement)) return '#FFFFFF';
  return /^#[0-9A-F]{6}$/i.test(input.value) ? input.value.toUpperCase() : '#FFFFFF';
}

class QuotePreviewController {
  previews = new Set();
  abortController = new AbortController();
  resizeObserver = new ResizeObserver((entries) => {
    entries.forEach(({ target }) => this.fit(target));
  });

  constructor(section) {
    this.section = section;
    const { signal } = this.abortController;
    section.addEventListener('change', this.handleChange, { signal });
    section.addEventListener('input', this.handleChange, { signal });
    section.addEventListener('quote:preview-change', this.handleChange, { signal });
    section.addEventListener('shopify:product:select', this.handleProductSelect, { signal });
  }

  register(preview) {
    this.previews.add(preview);
    this.resizeObserver.observe(preview);
    this.update();
  }

  unregister(preview) {
    this.previews.delete(preview);
    this.resizeObserver.unobserve(preview);
  }

  handleChange = () => requestAnimationFrame(() => this.update());

  handleProductSelect = (event) => {
    if (event.promise instanceof Promise) {
      event.promise.then(() => requestAnimationFrame(() => this.update())).catch(() => {});
      return;
    }
    requestAnimationFrame(() => this.update());
  };

  async update() {
    const backgroundName = selectedBackground(this.section);
    const background = CUSTOM_COLOUR_VALUES.has(backgroundName)
      ? customHex(this.section)
      : COLOURS.get(backgroundName) || '#FFFFFF';
    const fontStyle = selectedFont(this.section);
    const cursive = fontStyle.startsWith('cursive');
    const colour = fontStyle.endsWith('white') ? '#FFFFFF' : '#000000';
    const family = cursive ? 'PnP Palmaton' : 'PnP Playfair';
    const weight = cursive ? '400' : '700';

    await document.fonts?.load(`${weight} 24px "${family}"`).catch(() => {});
    this.previews.forEach((preview) => {
      preview.dataset.fontStyle = fontStyle;
      preview.style.backgroundColor = background;
      preview.style.color = colour;
      preview.style.fontFamily = `"${family}"`;
      preview.style.fontWeight = weight;
      this.fit(preview);
    });
  }

  fit(preview) {
    if (!(preview instanceof HTMLElement) || preview.clientWidth === 0 || preview.clientHeight === 0) return;
    const lines = [...preview.querySelectorAll('.quote-live-preview__line')].map((line) => line.textContent?.trim() || '');
    if (!lines.length) return;

    const cursive = preview.dataset.fontStyle?.startsWith('cursive');
    const family = cursive ? 'PnP Palmaton' : 'PnP Playfair';
    const weight = cursive ? '400' : '700';
    const lineHeight = cursive ? 0.98 : 1.04;
    const maxWidth = preview.clientWidth * 0.92;
    const maxHeight = preview.clientHeight * (cursive ? 0.56 : 0.5);
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) return;

    let low = 8;
    let high = preview.clientWidth * 0.52;
    for (let index = 0; index < 14; index += 1) {
      const size = (low + high) / 2;
      context.font = `${weight} ${size}px "${family}"`;
      const widest = Math.max(...lines.map((line) => context.measureText(line).width));
      const totalHeight = size * lineHeight * lines.length;
      if (widest <= maxWidth && totalHeight <= maxHeight) low = size;
      else high = size;
    }

    preview.style.setProperty('--quote-preview-font-size', `${low}px`);
    preview.style.setProperty('--quote-preview-line-height', String(lineHeight));
  }
}

class QuoteLivePreview extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.shopify-section');
    if (!(this.section instanceof HTMLElement)) return;
    if (!controllers.has(this.section)) controllers.set(this.section, new QuotePreviewController(this.section));
    controllers.get(this.section)?.register(this);
  }

  disconnectedCallback() {
    if (this.section instanceof HTMLElement) controllers.get(this.section)?.unregister(this);
  }
}

if (!customElements.get('quote-live-preview')) {
  customElements.define('quote-live-preview', QuoteLivePreview);
}
